/* v8 ignore start */
import {JsonModel} from "@/lib/core/jsonModel";
import {Effect, Fit} from "@/lib/lottie/layers/photoEffects";
import {PhotoModel} from "@/models/photoModel";
import {PointModel} from "@/models/pointModel";
import {RectangleModel} from "@/models/rectangleModel";

export class PhotoKeyFrames extends JsonModel<PhotoKeyFrames.IJson> {
  public get startScale(): PointModel {
    return new PointModel(this.json.startScale);
  }

  public get endScale(): PointModel {
    return new PointModel(this.json.endScale);
  }

  public zoom(zoomFactor: number): this {
    return this.update({
      startScale: {
        x: this.startScaleX * zoomFactor,
        y: this.startScaleY * zoomFactor,
      },
      endScale: {
        x: this.endScaleX * zoomFactor,
        y: this.endScaleY * zoomFactor,
      },
    });
  }

  public zoomAtTheStart(zoomFactor: number): this {
    return this.update({
      startScale: {
        x: this.startScaleX * zoomFactor,
        y: this.startScaleY * zoomFactor,
      },
    });
  }

  public zoomAtTheEnd(zoomFactor: number): this {
    return this.update({
      endScale: {
        x: this.endScaleX * zoomFactor,
        y: this.endScaleY * zoomFactor,
      },
    });
  }

  public zoomTowardsFocusPoint(
    photo: PhotoModel,
    focusPoint: PointModel,
    clip: RectangleModel,
    zoomFactor: number,
    atStart: boolean = false
  ) {
    const previousScaleX = atStart ? this.startScaleX / 100 : this.endScaleX / 100;
    const previousScaleY = atStart ? this.startScaleY / 100 : this.endScaleY / 100;

    const endScaleX = previousScaleX * zoomFactor;
    const endScaleY = previousScaleY * zoomFactor;

    const scaledPhotoWidth = photo.largestThumbnailWidth * endScaleX;
    const scaledPhotoHeight = photo.largestThumbnailHeight * endScaleY;

    const maxShiftX = (scaledPhotoWidth - clip.width) / 2;
    const maxShiftY = (scaledPhotoHeight - clip.height) / 2;

    const previousPositionShiftX = clip.width / 2 - this.endPositionX;
    const previousPositionShiftY = clip.height / 2 - this.endPositionY;

    const scaledPointX = focusPoint.x * endScaleX;
    const scaledPointY = focusPoint.y * endScaleY;

    const distanceToPointX = scaledPhotoWidth / 2 - scaledPointX;
    const distanceToPointY = scaledPhotoHeight / 2 - scaledPointY;

    const allowedShiftX = maxShiftX - Math.abs(previousPositionShiftX);
    const allowedShiftY = maxShiftY - Math.abs(previousPositionShiftY);

    const deltaPositionX = Math.sign(distanceToPointX) * Math.min(Math.abs(distanceToPointX) / 2, allowedShiftX / 2);

    const deltaPositionY = Math.sign(distanceToPointY) * Math.min(Math.abs(distanceToPointY) / 2, allowedShiftY / 2);

    const newPositionX = this.endPositionX + deltaPositionX;
    const newPositionY = this.endPositionY + deltaPositionY;

    if (atStart) {
      return this.update({
        startPosition: {
          x: newPositionX,
          y: newPositionY,
        },
      }).zoomAtTheStart(zoomFactor);
    } else {
      return this.update({
        endPosition: {
          x: newPositionX,
          y: newPositionY,
        },
      }).zoomAtTheEnd(zoomFactor);
    }
  }

  public pickPossiblePanDirections(photo: PhotoModel): Effect[] {
    const normalizedFocusPoint = photo.normalizedFocusPoint;

    const effects = [];

    if (normalizedFocusPoint.x < 0.5) {
      effects.push(Effect.PanLeft);
    }

    if (normalizedFocusPoint.x > 0.5) {
      effects.push(Effect.PanRight);
    }

    if (normalizedFocusPoint.y < 0.5) {
      effects.push(Effect.PanUp);
    }

    if (normalizedFocusPoint.y > 0.5) {
      effects.push(Effect.PanDown);
    }

    return effects;
  }

  // Dry the code with zoom effect
  public computePanOffset(photo: PhotoModel, clip: RectangleModel, effect: Effect) {
    // TODO: Avoid repetition with zoomTowardsFocusPoint function
    const previousScaleX = this.endScaleX / 100;
    const previousScaleY = this.endScaleY / 100;

    const endScaleX = previousScaleX;
    const endScaleY = previousScaleY;

    const point = photo.focusPoint;

    const scaledPhotoWidth = photo.largestThumbnailWidth * endScaleX;
    const scaledPhotoHeight = photo.largestThumbnailHeight * endScaleY;

    const maxShiftX = (scaledPhotoWidth - clip.width) / 2;
    const maxShiftY = (scaledPhotoHeight - clip.height) / 2;

    const scaledPointX = point.x * endScaleX;
    const scaledPointY = point.y * endScaleY;

    const distanceToPointX = scaledPhotoWidth / 2 - scaledPointX;
    const distanceToPointY = scaledPhotoHeight / 2 - scaledPointY;

    const deltaPositionX = Math.min(distanceToPointX / 2, maxShiftX);
    const deltaPositionY = Math.min(distanceToPointY / 2, maxShiftY);

    // debugger;

    if (effect === Effect.PanLeft || effect === Effect.PanRight) {
      return deltaPositionX;
    } else {
      return deltaPositionY;
    }
  }

  public panLeft(panOffset: number) {
    return this.update({
      startPosition: {
        x: this.startPositionX - panOffset,
        y: this.startPositionY,
      },
    });
  }

  public panRight(panOffset: number) {
    return this.update({
      startPosition: {
        x: this.startPositionX + panOffset,
        y: this.startPositionY,
      },
    });
  }

  public panUp(panOffset: number) {
    return this.update({
      startPosition: {
        x: this.startPositionX,
        y: this.startPositionY - panOffset,
      },
    });
  }

  public panDown(panOffset: number) {
    return this.update({
      startPosition: {
        x: this.startPositionX,
        y: this.startPositionY + panOffset,
      },
    });
  }

  public fitPhoto(photo: PhotoModel, preferredFit: Fit, clipWidth: number, clipHeight: number): this {
    let scale = 1;
    const focusPoint = photo.focusPoint;

    if (preferredFit === Fit.Cover) {
      scale = clipWidth / photo.largestThumbnailWidth;
    } else {
      scale = clipHeight / photo.largestThumbnailHeight;
    }

    const positionX = this.startPositionX; // will not change on X for now
    let positionY = this.startPositionY;

    if (preferredFit === Fit.Cover) {
      // Center the photo on Y axis towards focus point
      const scaledPhotoHeight = photo.largestThumbnailHeight * scale;
      const faceFocusPointY = focusPoint.y * scale;

      const distanceToFocusPointY = scaledPhotoHeight / 2 - faceFocusPointY;
      const maxShiftY = (scaledPhotoHeight - clipHeight) / 2;

      positionY += Math.sign(distanceToFocusPointY) * Math.min(Math.abs(distanceToFocusPointY), maxShiftY);
    }

    const lottieScale = scale * 100;

    return this.update({
      startScale: {x: lottieScale, y: lottieScale},
      endScale: {x: lottieScale, y: lottieScale},
      startPosition: {x: positionX, y: positionY},
      endPosition: {x: positionX, y: positionY},
    });
  }

  public get startPositionX(): number {
    return this.json.startPosition.x;
  }

  public get startPositionY(): number {
    return this.json.startPosition.y;
  }

  public get endPositionX(): number {
    return this.json.endPosition.x;
  }

  public get endPositionY(): number {
    return this.json.endPosition.y;
  }

  public get startScaleX(): number {
    return this.json.startScale.x;
  }

  public get startScaleY(): number {
    return this.json.startScale.y;
  }

  public get endScaleX(): number {
    return this.json.endScale.x;
  }

  public get endScaleY(): number {
    return this.json.endScale.y;
  }
}

export namespace PhotoKeyFrames {
  export interface IJson {
    startPosition: IKeyFramePosition;
    endPosition: IKeyFramePosition;
    startScale: IKeyFramePosition;
    endScale: IKeyFramePosition;
  }

  export interface IKeyFramePosition {
    x: number;
    y: number;
  }
}
/* v8 ignore stop */
